import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './apiClient';

export type NotificationCategory = 'System' | 'Workflow' | 'Performance' | 'Report' | 'Reminder' | 'Escalation' | 'Initiative' | 'Risk' | 'Milestone' | 'Accounting' | 'Stock' | 'Hr' | 'Fleet' | 'Parking' | 'Learning';
export type NotificationLevel = 'Info' | 'Success' | 'Warning' | 'Action';

export interface NotificationItem {
  id: string;
  title: string;
  message: string;
  category: NotificationCategory;
  level: NotificationLevel;
  link: string | null;
  isRead: boolean;
  createdDate: string;
}

export interface NotificationInbox {
  items: NotificationItem[];
  unreadCount: number;
}

export interface NotificationPreference {
  inAppEnabled: boolean;
  emailEnabled: boolean;
  teamsEnabled: boolean;
  importantOnly: boolean;
  /** Text messages for important notices (docs §184); only offered when the deployment has an SMS gateway. */
  smsEnabled?: boolean;
  smsAvailable?: boolean;
}

// ----------------------------- Queries -----------------------------
export function useNotifications(take = 30) {
  return useQuery({
    queryKey: ['notifications', 'inbox', take],
    queryFn: async () => (await apiClient.get<NotificationInbox>('/api/v1/notifications', { params: { take: String(take) } })).data,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => (await apiClient.get<number>('/api/v1/notifications/unread-count')).data,
    // Real-time push keeps this fresh; the interval is a safety net.
    refetchInterval: 60_000,
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: async () => (await apiClient.get<NotificationPreference>('/api/v1/notifications/preferences')).data,
  });
}

// ----------------------------- Mutations -----------------------------
function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] });
  qc.invalidateQueries({ queryKey: ['notifications', 'unread'] });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.post(`/api/v1/notifications/${id}/read`, {}); },
    onSuccess: () => invalidate(qc),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => { await apiClient.post('/api/v1/notifications/read-all', {}); },
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NotificationPreference) =>
      (await apiClient.put<NotificationPreference>('/api/v1/notifications/preferences', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'preferences'] }),
  });
}
